import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { format } from "date-fns";
import { Paperclip, Eye, EyeOff, CalendarIcon } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import { api } from "@/lib/pp/api";
import { toLocalYMD, fromYMD } from "@/lib/pp/dates";

/**
 * Minimal invoice upload dialog. Opens when the user taps the quick
 * "Attach PDF" / "Replace" button on a record row. Shows the chosen file
 * name and asks for the same PIN used on record save. No popup prompts.
 */
export default function InvoiceUploadDialog({
  open,
  onOpenChange,
  target,     // { recordId, file } | null
  onUploaded, // () => void — parent refreshes list
}) {
  const [pin, setPin] = useState("");
  const [showPin, setShowPin] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pinError, setPinError] = useState(false);
  const [submittedDate, setSubmittedDate] = useState(() => toLocalYMD(new Date()));
  const [submittedDateOpen, setSubmittedDateOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPin("");
    setShowPin(false);
    setPinError(false);
    setSubmittedDate(toLocalYMD(new Date()));
  }, [open]);

  const upload = async () => {
    if (!target?.file || !target?.recordId) return;
    if (!pin.trim()) {
      setPinError(true);
      toast.error("Enter the authorization PIN to upload");
      return;
    }
    setBusy(true);
    setPinError(false);
    try {
      const fd = new FormData();
      fd.append("file", target.file);
      if (submittedDate) fd.append("submitted_date", submittedDate);
      const { data } = await api.post(
        `/records/${target.recordId}/invoice`,
        fd,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            "X-Auth-Pin": pin,
          },
        },
      );
      toast.success(`Invoice attached · ${data.filename}`);
      onUploaded?.();
      onOpenChange(false);
    } catch (e) {
      if (e?.response?.status === 401) {
        setPinError(true);
      } else {
        toast.error(e?.response?.data?.detail || "Upload failed");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-md bg-white w-[calc(100vw-1.5rem)] overflow-x-hidden"
        data-testid="pp-invoice-upload-dialog"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <DialogHeader className="text-left">
          <DialogTitle
            className="text-[#1C2B26]"
            style={{ fontFamily: "'Work Sans', system-ui, sans-serif" }}
          >
            Upload invoice
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-wider text-[#4A5D55]">
              File
            </Label>
            <div
              className="flex items-center gap-2 h-10 px-3 rounded-md border border-dashed border-[#8EBCA9] bg-[#F5F9F7] text-sm text-[#2D5A4C] min-w-0"
              data-testid="pp-invoice-upload-filename"
            >
              <Paperclip className="h-3.5 w-3.5 shrink-0" />
              <span className="flex-1 min-w-0 truncate">
                {target?.file?.name || "(no file)"}
              </span>
            </div>
          </div>

          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-wider text-[#4A5D55]">
              Submitted date *
            </Label>
            <Popover open={submittedDateOpen} onOpenChange={setSubmittedDateOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  data-testid="pp-invoice-upload-submitted-date"
                  className="h-10 w-full inline-flex items-center gap-2 rounded-md border border-[#E5E2DC] bg-white px-3 text-sm text-[#1C2B26]"
                >
                  <CalendarIcon className="h-3.5 w-3.5 text-[#4A5D55] shrink-0" />
                  <span className="truncate flex-1 text-left">
                    {submittedDate
                      ? format(fromYMD(submittedDate), "MMM d, yyyy")
                      : "Pick date"}
                  </span>
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={fromYMD(submittedDate)}
                  onSelect={(d) => {
                    if (d) setSubmittedDate(toLocalYMD(d));
                    setSubmittedDateOpen(false);
                  }}
                />
              </PopoverContent>
            </Popover>
          </div>

          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-wider text-[#4A5D55]">
              Authorization PIN *
            </Label>
            <div className="relative">
              <Input
                type={showPin ? "text" : "password"}
                inputMode="numeric"
                autoComplete="off"
                value={pin}
                onChange={(e) => {
                  setPin(e.target.value);
                  if (pinError) setPinError(false);
                }}
                placeholder="Enter PIN to authorize this upload"
                data-testid="pp-invoice-upload-pin"
                className={
                  "pr-9 " +
                  (pinError
                    ? "border-[#B84A4A] ring-1 ring-[#B84A4A]/40 focus-visible:ring-[#B84A4A]"
                    : "")
                }
              />
              <button
                type="button"
                tabIndex={-1}
                onClick={() => setShowPin((v) => !v)}
                aria-label={showPin ? "Hide PIN" : "Show PIN"}
                data-testid="pp-invoice-upload-pin-toggle"
                className="absolute inset-y-0 right-2 flex items-center text-[#4A5D55] hover:text-[#0F3B2E]"
              >
                {showPin ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {pinError && (
              <p className="text-[10px] text-[#B84A4A]" data-testid="pp-invoice-upload-pin-error">
                Incorrect PIN — enter the same PIN used to sign in.
              </p>
            )}
          </div>
        </div>

        <div className="flex flex-row items-center justify-end gap-2 pt-2">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={busy}
            data-testid="pp-invoice-upload-cancel"
            className="h-9 px-4 text-sm"
          >
            Cancel
          </Button>
          <Button
            onClick={upload}
            disabled={busy}
            className="h-9 px-4 text-sm bg-[#0F3B2E] hover:bg-[#0A2A20] text-white"
            data-testid="pp-invoice-upload-submit"
          >
            {busy ? "Uploading…" : "Upload"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
